/**
 * Pure helpers for the QR / barcode tools: payload builders (WiFi, vCard,
 * mailto, sms, tel), WCAG contrast, file-name slugs, GS1 check digits and
 * friendly parsing of scanned text. No DOM, so everything is unit-tested.
 */

// ---------- payload builders ----------

/** WiFi QR values escape \ ; , : " (the Android/iOS WIFI: format). */
export function escapeWifi(s: string): string {
  return s.replace(/([\\;,:"])/g, "\\$1");
}

export type WifiSecurity = "WPA" | "WEP" | "nopass";

export function wifiPayload(o: { ssid: string; password: string; security: WifiSecurity; hidden: boolean }): string {
  const parts = [`WIFI:T:${o.security === "nopass" ? "nopass" : o.security}`, `S:${escapeWifi(o.ssid)}`];
  if (o.security !== "nopass") parts.push(`P:${escapeWifi(o.password)}`);
  if (o.hidden) parts.push("H:true");
  return parts.join(";") + ";;";
}

function vEscape(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/\r?\n/g, "\\n").replace(/;/g, "\;").replace(/,/g, "\\,");
}

export function vcardPayload(o: { first: string; last: string; phone: string; email: string; company: string; website: string }): string {
  const lines = ["BEGIN:VCARD", "VERSION:3.0", `N:${vEscape(o.last)};${vEscape(o.first)};;;`, `FN:${vEscape([o.first, o.last].filter(Boolean).join(" "))}`];
  if (o.company.trim()) lines.push(`ORG:${vEscape(o.company.trim())}`);
  if (o.phone.trim()) lines.push(`TEL;TYPE=CELL:${o.phone.trim()}`);
  if (o.email.trim()) lines.push(`EMAIL:${o.email.trim()}`);
  if (o.website.trim()) lines.push(`URL:${o.website.trim()}`);
  lines.push("END:VCARD");
  return lines.join("\n");
}

export function mailtoPayload(o: { to: string; subject: string; body: string }): string {
  const q: string[] = [];
  if (o.subject) q.push(`subject=${encodeURIComponent(o.subject)}`);
  if (o.body) q.push(`body=${encodeURIComponent(o.body)}`);
  return `mailto:${o.to.trim()}${q.length ? "?" + q.join("&") : ""}`;
}

export const smsPayload = (phone: string, body: string) => `SMSTO:${phone.trim()}:${body}`;
export const telPayload = (phone: string) => `tel:${phone.trim()}`;

// ---------- colour ----------

export function parseHex(hex: string): [number, number, number] | null {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = h.split("").map(c => c + c).join("");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

export function luminance(hex: string): number {
  const rgb = parseHex(hex) ?? [0, 0, 0];
  const [r, g, b] = rgb.map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string): number {
  const la = luminance(a), lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Warning text for a foreground/background pair, or null when fine. */
export function contrastWarning(fg: string, bg: string): string | null {
  if (luminance(fg) > luminance(bg)) {
    return "The code is lighter than its background (inverted). Many phone scanners cannot read inverted codes. Use a dark code on a light background.";
  }
  const r = contrastRatio(fg, bg);
  if (r < 4) {
    return `Low contrast (${r.toFixed(1)}:1). Printed codes with less than about 4:1 often fail to scan. Make the code darker or the background lighter.`;
  }
  return null;
}

// ---------- file names ----------

export function slugify(value: string, max = 60): string {
  const s = value
    .trim()
    .replace(/^https?:\/\//i, "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, max)
    .replace(/-+$/g, "");
  return s || "code";
}

export function batchFileName(index: number, total: number, value: string, ext = "png"): string {
  const width = Math.max(3, String(total).length);
  return `${String(index + 1).padStart(width, "0")}-${slugify(value)}.${ext}`;
}

/** Parses bulk input. Each line is either "value" or "name,value" (first comma, only when the
 *  part before it looks like a name and the part after is non-empty and the line is not a URL/text with the comma inside). */
export interface BulkRow { name: string | null; value: string }
export function parseBulkLines(input: string, namedMode: boolean): { rows: BulkRow[]; duplicates: number; blank: number } {
  const seen = new Set<string>();
  const rows: BulkRow[] = [];
  let duplicates = 0, blank = 0;
  for (const raw of input.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) { blank++; continue; }
    let name: string | null = null;
    let value = line;
    if (namedMode) {
      const i = line.indexOf(",");
      if (i >= 0 && line.slice(i + 1).trim()) { name = line.slice(0, i).trim() || null; value = line.slice(i + 1).trim(); } // ",value" = empty name, the leading comma is a separator, not part of the code
    }
    if (seen.has(value)) { duplicates++; continue; }
    seen.add(value);
    rows.push({ name, value });
  }
  return { rows, duplicates, blank };
}

/** Values longer than this make a dense QR code that may not scan from a small print or a phone screen. */
export const LONG_QR_VALUE = 300;
export function longQrValues(rows: { value: string }[]): number {
  return rows.filter(r => r.value.length > LONG_QR_VALUE).length;
}

// ---------- barcode validation ----------

export function gs1CheckDigit(body: string): number {
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    const d = Number(body[body.length - 1 - i]);
    sum += i % 2 === 0 ? d * 3 : d;
  }
  return (10 - (sum % 10)) % 10;
}

const NUMERIC_SPEC: Record<string, { name: string; body: number }> = {
  EAN13: { name: "EAN-13", body: 12 },
  EAN8: { name: "EAN-8", body: 7 },
  UPC: { name: "UPC-A", body: 11 },
  ITF14: { name: "ITF-14", body: 13 },
};

/** A specific, human message when the value cannot be encoded in this format, else null. */
export function barcodeProblem(value: string, format: string): string | null {
  const v = value.trim();
  if (!v) return null;
  const spec = NUMERIC_SPEC[format];
  if (spec) {
    if (!/^\d+$/.test(v)) return `${spec.name} uses digits only (0-9). Remove letters, spaces and symbols.`;
    if (v.length !== spec.body && v.length !== spec.body + 1) {
      return `${spec.name} needs ${spec.body} digits (the check digit is added for you) or ${spec.body + 1} digits including it. You entered ${v.length}.`;
    }
    if (v.length === spec.body + 1) {
      const want = gs1CheckDigit(v.slice(0, spec.body));
      if (Number(v[spec.body]) !== want) return `Check digit should be ${want}, not ${v[spec.body]}. Enter ${spec.body} digits and it is added for you.`;
    }
    return null;
  }
  if (format === "CODE39") {
    if (!/^[A-Z0-9 \-.$/+%]+$/i.test(v)) return "Code 39 accepts letters A-Z, digits, space and - . $ / + % only.";
    return null;
  }
  if (format === "CODE128") {
    if (/[^\x20-\x7e]/.test(v)) return "Code 128 accepts standard keyboard characters only (letters, digits, symbols; no accents or emoji).";
    return null;
  }
  return null;
}

/** Download name that says what is inside, e.g. ean13-5901234123457.png (check digit included when the tool adds it). */
export function barcodeFileName(value: string, format: string, ext: "png" | "svg"): string {
  let v = value.trim();
  const spec = NUMERIC_SPEC[format];
  if (spec && /^\d+$/.test(v) && v.length === spec.body) v += String(gs1CheckDigit(v));
  const safe = v.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
  const fmt = format === "UPC" ? "upca" : format.toLowerCase();
  return safe ? `${fmt}-${safe}.${ext}` : `${fmt}.${ext}`;
}

export interface BarcodeListRow { line: number; value: string; file: string }
export interface BarcodeListProblem { line: number; input: string; message: string }

/**
 * A pasted list of barcode values, one per line (B-0289). Every line is checked with the same rules as the single
 * barcode, so a row with a wrong check digit is reported with the reason and left out of the ZIP, never silently fixed.
 * Repeated values are made once; each file is named by its value (check digit included) and clashes get -2, -3.
 */
export function parseBarcodeList(text: string, format: string, ext: "png" | "svg"): { rows: BarcodeListRow[]; problems: BarcodeListProblem[]; duplicates: number } {
  const rows: BarcodeListRow[] = [];
  const problems: BarcodeListProblem[] = [];
  const seen = new Set<string>();
  const names = new Set<string>();
  let duplicates = 0;
  text.split(/\r?\n/).forEach((raw, i) => {
    const value = raw.trim();
    if (!value) return;
    const line = i + 1;
    const message = barcodeProblem(value, format);
    if (message) { problems.push({ line, input: value, message }); return; }
    // The same number with and without its check digit is one barcode; compare full values, not (shortened) file names.
    const spec = NUMERIC_SPEC[format];
    const key = format === "CODE39" ? value.toUpperCase() : spec && value.length === spec.body ? value + gs1CheckDigit(value) : value;
    if (seen.has(key)) { duplicates++; return; }
    seen.add(key);
    let file = barcodeFileName(value, format, ext);
    for (let n = 2; names.has(file.toLowerCase()); n++) file = barcodeFileName(value, format, ext).replace(/(\.[a-z]+)$/, `-${n}$1`);
    names.add(file.toLowerCase());
    rows.push({ line, value, file });
  });
  return { rows, problems, duplicates };
}

/** Formats whose numbers are issued by GS1 for goods sold through shops and marketplaces. */
export const GS1_FORMATS = ["EAN13", "EAN8", "UPC", "ITF14"];

// ---------- scanned text ----------

export type ScanKind = "url" | "wifi" | "vcard" | "email" | "sms" | "phone" | "text";

const SHORTENERS = new Set(["bit.ly", "t.co", "tinyurl.com", "goo.gl", "ow.ly", "is.gd", "buff.ly", "rebrand.ly", "cutt.ly", "shorturl.at", "tiny.cc", "rb.gy"]);

export interface ScanInfo {
  kind: ScanKind;
  url?: URL;
  warning?: string;
  wifi?: { ssid: string; password: string; security: string; hidden: boolean };
  contact?: { name: string; phones: string[]; emails: string[]; org: string; url: string };
  to?: string;
  body?: string;
  subject?: string;
}

function splitUnescaped(s: string, sep: string): string[] {
  const out: string[] = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && i + 1 < s.length) { cur += s[i] + s[i + 1]; i++; }
    else if (s[i] === sep) { out.push(cur); cur = ""; }
    else cur += s[i];
  }
  out.push(cur);
  return out;
}
const unescapeWifi = (s: string) => s.replace(/\\([\\;,:"])/g, "$1");

export function classifyScan(text: string): ScanInfo {
  const t = text.trim();
  if (/^WIFI:/i.test(t)) {
    const fields: Record<string, string> = {};
    for (const part of splitUnescaped(t.slice(5), ";")) {
      const i = part.indexOf(":");
      if (i > 0) fields[part.slice(0, i).toUpperCase()] = unescapeWifi(part.slice(i + 1));
    }
    return { kind: "wifi", wifi: { ssid: fields.S ?? "", password: fields.P ?? "", security: fields.T || "nopass", hidden: /^true$/i.test(fields.H ?? "") } };
  }
  if (/^BEGIN:VCARD/i.test(t)) {
    const c = { name: "", phones: [] as string[], emails: [] as string[], org: "", url: "" };
    for (const line of t.split(/\r?\n/)) {
      const i = line.indexOf(":");
      if (i < 0) continue;
      const key = line.slice(0, i).split(";")[0].toUpperCase();
      const val = line.slice(i + 1).replace(/\\n/gi, " ").replace(/\\([;,\\])/g, "$1").trim();
      if (key === "FN") c.name = val;
      else if (key === "N" && !c.name) c.name = val.split(";").filter(Boolean).reverse().join(" ");
      else if (key === "TEL") c.phones.push(val);
      else if (key === "EMAIL") c.emails.push(val);
      else if (key === "ORG") c.org = val.replace(/;/g, " ").trim();
      else if (key === "URL") c.url = val;
    }
    return { kind: "vcard", contact: c };
  }
  if (/^mailto:/i.test(t)) {
    try {
      const u = new URL(t);
      return { kind: "email", to: decodeURIComponent(u.pathname), subject: u.searchParams.get("subject") ?? "", body: u.searchParams.get("body") ?? "" };
    } catch { /* fall through */ }
  }
  const sms = /^(?:SMSTO|SMS):([^:?]*)[:?]?(.*)$/i.exec(t);
  if (sms) return { kind: "sms", to: sms[1], body: sms[2].replace(/^body=/i, "") };
  if (/^tel:/i.test(t)) return { kind: "phone", to: t.slice(4) };
  try {
    const u = new URL(t);
    if (u.protocol === "http:" || u.protocol === "https:") {
      const host = u.hostname.replace(/^www\./, "");
      let warning: string | undefined;
      if (u.protocol === "http:") warning = "This link is not encrypted (http). Only open it if you trust the source.";
      else if (SHORTENERS.has(host)) warning = "This is a shortened link, so the real destination is hidden. Only open it if you trust the source.";
      return { kind: "url", url: u, warning };
    }
  } catch { /* plain text */ }
  return { kind: "text" };
}

export function contactToVcf(c: { name: string; phones: string[]; emails: string[]; org: string; url: string }): string {
  const lines = ["BEGIN:VCARD", "VERSION:3.0", `FN:${c.name}`];
  if (c.org) lines.push(`ORG:${c.org}`);
  c.phones.forEach(p => lines.push(`TEL:${p}`));
  c.emails.forEach(e => lines.push(`EMAIL:${e}`));
  if (c.url) lines.push(`URL:${c.url}`);
  lines.push("END:VCARD");
  return lines.join("\r\n");
}
